import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from 'sonner';
import { ProtectedRoute } from '@/components/shared/ProtectedRoute';
import { useTenant } from '@/hooks/useTenant';
import { LoadingSpinner } from '@/components/shared/LoadingStates';

// Pages
import { Login } from '@/pages/Login';
import { Register } from '@/pages/customer/Register';
import { UserProfilePage } from '@/pages/shared/Profile';

// Customer
import { CustomerDashboard } from '@/pages/customer/Dashboard';
import { Tickets } from '@/pages/customer/Tickets';
import { NewTicket } from '@/pages/customer/NewTicket';
import { TicketDetail } from '@/pages/customer/TicketDetail';
import { MyProducts } from '@/pages/customer/MyProducts';

// Agent
import { AgentDashboard } from '@/pages/agent/Dashboard';
import { TicketReview } from '@/pages/agent/TicketReview';
import { AgentLookup } from '@/pages/agent/Lookup';
import { AgentMyTickets } from '@/pages/agent/MyTickets';

// FOC
import { FocDashboard } from '@/pages/foc/Dashboard';
import { AssignTechnician } from '@/pages/foc/AssignTechnician';
import { FocAllTickets } from '@/pages/foc/FocAllTickets';
import { FocTechnicians } from '@/pages/foc/FocTechnicians';

// Technician
import { TechnicianDashboard } from '@/pages/technician/Dashboard';
import { JobCard } from '@/pages/technician/JobCard';

import { useCurrentUser } from '@/hooks/useAuth';
import { getAuthToken, getUserRole, getLastPortal } from '@/lib/auth';

// Admin
import { AdminDashboard } from '@/pages/admin/Dashboard';
import { AdminTickets } from '@/pages/admin/Tickets';
import { AdminTicketDetail } from '@/pages/admin/AdminTicketDetail';
import { AdminUsers } from '@/pages/admin/Users';
import { AdminProducts } from '@/pages/admin/Products';
import { AdminAnalytics } from '@/pages/admin/Analytics';
import { AdminConfig } from '@/pages/admin/Config';
import { WhatsAppHub } from '@/pages/admin/WhatsAppHub';

const NotFoundRedirect = () => {
  const token = getAuthToken();
  const userRole = getUserRole();

  if (token && userRole) {
    const roleRoutes = {
      customer: '/customer',
      agent: '/ops/agent',
      foc: '/ops/foc',
      technician: '/ops/tech',
      super_admin: '/ops/admin',
    };
    // Fallback uses stored portal to avoid sending ops users to /login
    const fallback = getLastPortal() === 'ops' ? '/ops/login' : '/login';
    return <Navigate to={roleRoutes[userRole] || fallback} replace />;
  }

  // No token — use stored portal preference, not window.location (unreliable after refresh)
  const loginPath = getLastPortal() === 'ops' ? '/ops/login' : '/login';
  return <Navigate to={loginPath} replace />;
};

// The bare domain is customer-facing by default — unlike unmatched/mistyped paths
// (NotFoundRedirect above), it should never be swayed by a stale "last portal"
// preference from previous ops testing on this browser. /ops/login stays reachable
// as its own explicit route regardless.
const RootRedirect = () => {
  const token = getAuthToken();
  const userRole = getUserRole();

  if (token && userRole) {
    const roleRoutes = {
      customer: '/customer',
      agent: '/ops/agent',
      foc: '/ops/foc',
      technician: '/ops/tech',
      super_admin: '/ops/admin',
    };
    const fallback = getLastPortal() === 'ops' ? '/ops/login' : '/login';
    return <Navigate to={roleRoutes[userRole] || fallback} replace />;
  }

  return <Navigate to="/login" replace />;
};

function App() {
  const { loading, error } = useTenant();
  useCurrentUser();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-muted/30 p-6 text-center">
        <h1 className="text-4xl font-black mb-4">KWICC</h1>
        <p className="text-muted-foreground mb-4">Failed to load tenant configuration. Please check the subdomain URL.</p>
        <p className="text-xs text-muted-foreground/60">Error Details: {error.message}</p>
      </div>
    );
  }

  return (
    <>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          
          {/* Operations Public Login */}
          <Route path="/ops/login" element={<Login />} />
          
          {/* Customer Routes */}
          <Route path="/customer" element={
            <ProtectedRoute allowedRoles={['customer']}>
              <CustomerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/customer/tickets" element={
            <ProtectedRoute allowedRoles={['customer']}>
              <Tickets />
            </ProtectedRoute>
          } />
          <Route path="/customer/tickets/new" element={
            <ProtectedRoute allowedRoles={['customer']}>
              <NewTicket />
            </ProtectedRoute>
          } />
          <Route path="/customer/tickets/:id" element={
            <ProtectedRoute allowedRoles={['customer']}>
              <TicketDetail />
            </ProtectedRoute>
          } />
          <Route path="/customer/products" element={
            <ProtectedRoute allowedRoles={['customer']}>
              <MyProducts />
            </ProtectedRoute>
          } />
          <Route path="/customer/profile" element={
            <ProtectedRoute allowedRoles={['customer']}>
              <UserProfilePage />
            </ProtectedRoute>
          } />
          
          {/* Agent Routes */}
          <Route path="/ops/agent" element={
            <ProtectedRoute allowedRoles={['agent', 'super_admin']}>
              <AgentDashboard />
            </ProtectedRoute>
          } />
          <Route path="/ops/agent/tickets/:id" element={
            <ProtectedRoute allowedRoles={['agent', 'super_admin']}>
              <AdminTicketDetail />
            </ProtectedRoute>
          } />
          <Route path="/ops/agent/my-tickets" element={
            <ProtectedRoute allowedRoles={['agent', 'super_admin']}>
              <AgentMyTickets />
            </ProtectedRoute>
          } />
          <Route path="/ops/agent/lookup" element={
            <ProtectedRoute allowedRoles={['agent', 'super_admin']}>
              <AgentLookup />
            </ProtectedRoute>
          } />
          
          {/* FOC Routes */}
          <Route path="/ops/foc" element={
            <ProtectedRoute allowedRoles={['foc', 'super_admin']}>
              <FocDashboard />
            </ProtectedRoute>
          } />
          <Route path="/ops/foc/tickets" element={
            <ProtectedRoute allowedRoles={['foc', 'super_admin']}>
              <FocAllTickets />
            </ProtectedRoute>
          } />
          <Route path="/ops/foc/technicians" element={
            <ProtectedRoute allowedRoles={['foc', 'super_admin']}>
              <FocTechnicians />
            </ProtectedRoute>
          } />
          <Route path="/ops/foc/tickets/:id" element={
            <ProtectedRoute allowedRoles={['foc', 'super_admin']}>
              <AdminTicketDetail />
            </ProtectedRoute>
          } />
          <Route path="/ops/foc/tickets/:id/assign" element={
            <ProtectedRoute allowedRoles={['foc', 'super_admin']}>
              <AssignTechnician />
            </ProtectedRoute>
          } />
          
          {/* Technician Routes */}
          <Route path="/ops/tech" element={
            <ProtectedRoute allowedRoles={['technician']}>
              <TechnicianDashboard />
            </ProtectedRoute>
          } />
          <Route path="/ops/tech/jobs/:id" element={
            <ProtectedRoute allowedRoles={['technician']}>
              <JobCard />
            </ProtectedRoute>
          } />
          
          {/* Admin Routes */}
          <Route path="/ops/admin" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/ops/admin/tickets" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <AdminTickets />
            </ProtectedRoute>
          } />
          <Route path="/ops/admin/tickets/:id" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <AdminTicketDetail />
            </ProtectedRoute>
          } />
          <Route path="/ops/admin/users" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <AdminUsers />
            </ProtectedRoute>
          } />
          <Route path="/ops/admin/products" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <AdminProducts />
            </ProtectedRoute>
          } />
          <Route path="/ops/admin/analytics" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <AdminAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/ops/admin/config" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <AdminConfig />
            </ProtectedRoute>
          } />
          <Route path="/ops/admin/whatsapp" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <WhatsAppHub />
            </ProtectedRoute>
          } />
          <Route path="/ops/profile" element={
            <ProtectedRoute allowedRoles={['agent', 'foc', 'technician', 'super_admin']}>
              <UserProfilePage />
            </ProtectedRoute>
          } />
          
          {/* Root redirect */}
          <Route path="/" element={<RootRedirect />} />
          
          {/* 404 */}
          <Route path="*" element={<NotFoundRedirect />} />
        </Routes>
      </BrowserRouter>
      
      {/* Toast Notifications */}
      <Toaster position="top-right" richColors />
    </>
  );
}

export default App;
